import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "../lib/data";

// Nur die wichtigen Sektionen — Logo mittig, Nav rechts.
const NAV = [
  { id: "studios", label: "Studios" },
  { id: "leistungen", label: "Leistungen" },
  { id: "kontakt", label: "Kontakt" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const goTo = (id) => {
    setOpen(false);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, open ? 200 : 0);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-[#050505]/90 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-10 py-3 md:py-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3 md:gap-4 lg:gap-6">

        {/* LINKS: Mobile/Tablet Burger, Desktop leer */}
        <div className="flex items-center justify-start">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -ml-2 text-white"
            aria-label={open ? "Menü schließen" : "Menü öffnen"}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* MITTE: Logo */}
        <button
          data-testid="brand-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="group shrink-0 justify-self-center"
        >
          <img
            src={LOGO_URL}
            alt="STRAßENWERT"
            className="h-9 sm:h-10 md:h-11 lg:h-12 w-auto object-contain drop-shadow-[0_0_18px_rgba(255,0,51,0.4)] group-hover:drop-shadow-[0_0_28px_rgba(255,0,51,0.7)] transition-all duration-500"
          />
          <span className="sr-only">Straßenwert</span>
        </button>

        {/* RECHTS: Desktop-Nav + CTA, Mobile/Tablet nur CTA */}
        <div className="flex items-center justify-end gap-3 md:gap-4 lg:gap-6 xl:gap-8">
          {/* Desktop Nav (nur lg+) */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-${n.id}`}
                onClick={() => goTo(n.id)}
                className="text-[11px] font-semibold tracking-[0.25em] uppercase text-zinc-400 hover:text-white transition-colors relative group whitespace-nowrap"
              >
                {n.label}
                <span className="absolute -bottom-2 left-0 w-0 h-px bg-[#ff0033] group-hover:w-full transition-all duration-500 shadow-[0_0_8px_rgba(255,0,51,0.8)]" />
              </button>
            ))}
          </nav>

          {/* Bewerben-CTA (immer sichtbar) */}
          <button
            data-testid="cta-bewerbung-nav"
            onClick={() => goTo("kontakt")}
            className="inline-flex items-center gap-1.5 md:gap-2 text-[9px] md:text-[10px] font-bold tracking-[0.22em] md:tracking-[0.25em] uppercase border border-[#ff0033]/50 text-white px-3 md:px-4 lg:px-5 py-1.5 md:py-2 lg:py-2.5 hover:bg-[#ff0033] hover:shadow-[0_0_20px_rgba(255,0,51,0.4)] transition-all whitespace-nowrap"
          >
            <span className="red-led" />
            Bewerben
          </button>
        </div>
      </div>

      {/* Mobile/Tablet slide-down menu */}
      <div
        data-testid="mobile-menu"
        aria-hidden={!open}
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[80vh] opacity-100 border-t border-white/[0.06]" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="bg-[#050505]/95 backdrop-blur-xl px-6 py-7 flex flex-col gap-1">
          {NAV.map((n, i) => (
            <button
              key={n.id}
              data-testid={`mobile-nav-${n.id}`}
              onClick={() => goTo(n.id)}
              className={`group flex items-baseline gap-4 py-2 text-left transition-all duration-500 ${
                open ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-3"
              }`}
              style={{ transitionDelay: open ? `${80 + i * 50}ms` : "0ms" }}
            >
              <span className="text-[9px] tracking-[0.3em] uppercase text-[#ff0033] font-bold w-6">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl leading-none text-white group-hover:text-glow-red transition-all">
                {n.label}
              </span>
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
